import React from 'react';
import { Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import ScheduleIcon from '@material-ui/icons/Schedule';
import {
  Colors,
  CommunicationDisplay,
  CommunicationType,
  CommunicationTypes,
  ScheduledCommunication,
  ScheduledCommunicationPurposes,
  ScheduledCommunicationStatuses,
  ScheduledCommunicationTypes,
} from 'typings';
import { formatDate, formatTimezone, getLocalTimezone } from 'utils/functions';

interface ScheduledCommunicationProps extends CommunicationDisplay {
  data: ScheduledCommunication & { commType: CommunicationType; leadTimezone?: string | null };
  children: React.ReactNode;
  caption?: React.ReactElement;
}

interface StyleProps {
  isFailed: boolean;
  isInactive: boolean;
}

const DATE_FORMAT = 'ddd, MMM D, h:mm A';

const inactiveStatuses = [
  ScheduledCommunicationStatuses.CANCELLED,
  ScheduledCommunicationStatuses.SUPERSEDED,
];

const getSubject = ({ purpose, type }: ScheduledCommunication): string => {
  if (purpose === ScheduledCommunicationPurposes.FOLLOW_UP_REQUEST) {
    return type === ScheduledCommunicationTypes.CALL ? 'Follow-up call' : 'Follow-up SMS';
  }

  return type === ScheduledCommunicationTypes.CALL ? 'Call' : 'SMS';
};

const getOutcome = ({ status }: ScheduledCommunication): string =>
  status === ScheduledCommunicationStatuses.SUPERSEDED ? 'rescheduled' : status;

const useStyles = makeStyles((theme: Theme) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  summary: ({ isInactive }: StyleProps) => ({
    display: 'flex',
    alignItems: 'center',
    opacity: isInactive ? 0.5 : 1,
  }),
  icon: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    fill: Colors.ORANGE,
  },
  details: {
    fontWeight: 500,
    paddingLeft: theme.spacing(0.5),
    color: theme.palette.grey['500'],
  },
  trailingSeparator: {
    fontWeight: 500,
    color: theme.palette.grey['500'],
  },
  event: ({ isFailed }: StyleProps) => ({
    color: isFailed ? Colors.RED : theme.palette.text.primary,
  }),
  // matches the spacing Caption puts between its own items
  separator: {
    display: 'inline-block',
    padding: theme.spacing(0, 0.5),
  },
  caption: {
    '&& > *': {
      paddingLeft: 0,
    },
  },
}));

const ScheduledCommunicationContent = ({
  data,
  caption,
}: React.PropsWithChildren<ScheduledCommunicationProps>) => {
  const isOutcome = data.commType === CommunicationTypes.SCHEDULED_COMMUNICATION_OUTCOME;
  const isInactive = inactiveStatuses.includes(data.status);

  const timezone = data.leadTimezone ?? getLocalTimezone();
  const dueAt = `${formatDate(data.dueAt, DATE_FORMAT, timezone)} ${formatTimezone(timezone, data.dueAt)}`;

  const classes = useStyles({
    isFailed: isOutcome && data.status === ScheduledCommunicationStatuses.FAILED,
    isInactive,
  });

  return (
    <div className={classes.row}>
      <span className={classes.summary}>
        <ScheduleIcon className={classes.icon} />
        <Typography variant="caption" className={classes.details}>
          <span className={classes.event}>
            {`${getSubject(data)} ${isOutcome ? getOutcome(data) : 'scheduled'}`}
          </span>
          <span className={classes.separator}>&middot;</span>
          {isOutcome && isInactive ? <s>{dueAt}</s> : <span>{dueAt}</span>}
        </Typography>
      </span>
      <Typography variant="caption" className={classes.trailingSeparator}>
        <span className={classes.separator}>&middot;</span>
      </Typography>
      <span className={classes.caption}>{caption}</span>
    </div>
  );
};

export default ScheduledCommunicationContent;
